import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { leaveService, holidayService } from '../services/api.service';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectOption } from '../components/ui/select';
import { DatePicker } from '../components/ui/date-picker';
import AppLayout from '../layouts/AppLayout';

const LeaveRequestForm = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [formData, setFormData] = useState({
    startDate: null,
    endDate: null,
    leaveTypeId: '',
    reason: '',
    requestType: 'full-day' // New field: 'full-day', 'morning-half', 'afternoon-half'
  });
  
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [calculatedDays, setCalculatedDays] = useState(0);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [weekendDays, setWeekendDays] = useState(0);
  const [holidayDays, setHolidayDays] = useState(0);
  const [totalDays, setTotalDays] = useState(0);
  const [leaveStats, setLeaveStats] = useState({
    total: 20,
    used: 0,
    remaining: 0
  });
  
  // Check if current selection is half-day
  const isHalfDay = formData.requestType !== 'full-day';
  
  // Separate function to load leave types
  const loadLeaveTypes = async () => {
    try {
      const response = await leaveService.getLeaveTypes();
      console.log('Leave types response:', response);
      
      if (response && Array.isArray(response.leaveTypes)) {
        setLeaveTypes(response.leaveTypes);
        return true;
      } else {
        console.error('Invalid leave types format:', response);
        return false;
      }
    } catch (err) {
      console.error('Error loading leave types:', err);
      return false;
    }
  };
  
  // Calculate used leave days
  const calculateUsedDays = (leaveRequests) => {
    if (!leaveRequests || !Array.isArray(leaveRequests)) {
      return 0;
    }
    
    return leaveRequests
      .filter(request => request.status === 'approved' && !request.is_adjustment)
      .reduce((total, request) => {
        return total + (Number(request.working_days) || 0);
      }, 0);
  };
  
  // Fetch data function
  const fetchLeaveData = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) {
        setIsLoading(true);
        setError('');
      }
      
      console.log('Fetching fresh leave data...');
      
      const leaveTypesSuccess = await loadLeaveTypes();
      if (!leaveTypesSuccess) {
        throw new Error('Failed to load leave types');
      }
      
      const [holidaysRes, leaveRequestsRes] = await Promise.all([
        holidayService.getAllHolidays(new Date().getFullYear()),
        leaveService.getUserLeaveRequests()
      ]);
      
      let currentLeaveBalance = 0;
      if (user && user.leave_balance !== undefined) {
        currentLeaveBalance = Number(user.leave_balance);
        console.log('Using leave balance from auth context:', currentLeaveBalance);
      } else {
        console.warn('Leave balance not found in user object:', user);
      }
      
      if (holidaysRes && Array.isArray(holidaysRes.holidays)) {
        setHolidays(holidaysRes.holidays);
      } else {
        console.warn('Invalid holidays format or empty:', holidaysRes);
        setHolidays([]);
      }
      
      let usedDays = 0;
      if (leaveRequestsRes && Array.isArray(leaveRequestsRes.leaveRequests)) {
        usedDays = calculateUsedDays(leaveRequestsRes.leaveRequests);
      } else {
        console.warn('Invalid leave requests format or empty:', leaveRequestsRes);
      }
      
      const annualAllocation = 20;
      
      setLeaveStats({
        total: annualAllocation,
        used: usedDays,
        remaining: currentLeaveBalance
      });
      
      console.log('Updated leave stats:', {
        total: annualAllocation,
        used: usedDays,
        remaining: currentLeaveBalance
      });
      
      if (showLoading) {
        setIsLoading(false);
      }
      
      return {
        remaining: currentLeaveBalance,
        used: usedDays,
        total: annualAllocation
      };
    } catch (err) {
      console.error('Error fetching leave data:', err);
      setError(`Failed to load required data: ${err.message || 'Unknown error'}`);
      if (showLoading) {
        setIsLoading(false);
      }
      return null;
    }
  }, [user]);
  
  useEffect(() => {
    fetchLeaveData();
    
    const handleLeaveBalanceUpdate = () => {
      console.log('Leave balance update event detected, refreshing data');
      fetchLeaveData(false);
    };
    
    window.addEventListener('leave-balance-update', handleLeaveBalanceUpdate);
    
    return () => {
      window.removeEventListener('leave-balance-update', handleLeaveBalanceUpdate);
    };
  }, [fetchLeaveData, user]);
  
  // Calculate working days between dates (now supports half-days)
  useEffect(() => {
    const calculateWorkingDays = () => {
      if (!formData.startDate) {
        setCalculatedDays(0);
        setWeekendDays(0);
        setHolidayDays(0);
        setTotalDays(0);
        return;
      }
      
      // For half-days, we only need start date
      if (isHalfDay) {
        const startDate = new Date(formData.startDate);
        startDate.setHours(0, 0, 0, 0);
        
        const dayOfWeek = startDate.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        
        // Check if it's a holiday
        const holidayDates = holidays.map(h => {
          const date = new Date(h.date);
          date.setHours(0, 0, 0, 0);
          return date.toDateString();
        });
        const isHoliday = holidayDates.includes(startDate.toDateString());
        
        if (isWeekend) {
          setCalculatedDays(0);
          setWeekendDays(0.5); // Show 0.5 for half-day weekend
          setHolidayDays(0);
          setTotalDays(0.5); // Show 0.5 for half-day total
        } else if (isHoliday) {
          setCalculatedDays(0);
          setWeekendDays(0);
          setHolidayDays(0.5); // Show 0.5 for half-day holiday
          setTotalDays(0.5); // Show 0.5 for half-day total
        } else {
          setCalculatedDays(0.5); // Half day
          setWeekendDays(0);
          setHolidayDays(0);
          setTotalDays(0.5); // Show 0.5 for half-day total
        }
        return;
      }
      
      // Full multi-day calculation (existing logic)
      if (!formData.endDate) {
        setCalculatedDays(0);
        setWeekendDays(0);
        setHolidayDays(0);
        setTotalDays(0);
        return;
      }
      
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);
      
      if (endDate < startDate) {
        setCalculatedDays(0);
        setWeekendDays(0);
        setHolidayDays(0);
        setTotalDays(0);
        return;
      }
      
      let dayCount = 0;
      let weekendCount = 0;
      let holidayCount = 0;
      let totalCount = 0;
      
      const holidayDates = holidays.map(h => {
        const date = new Date(h.date);
        date.setHours(0, 0, 0, 0);
        return date.toDateString();
      });
      
      const currentDay = new Date(startDate);
      
      while (currentDay <= endDate) {
        totalCount++;
        const dayOfWeek = currentDay.getDay();
        const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
        const isHoliday = holidayDates.includes(currentDay.toDateString());
        
        if (isWeekend) {
          weekendCount++;
        } else if (isHoliday) {
          holidayCount++;
        } else {
          dayCount++;
        }
        
        currentDay.setDate(currentDay.getDate() + 1);
      }
      
      setCalculatedDays(dayCount);
      setWeekendDays(weekendCount);
      setHolidayDays(holidayCount);
      setTotalDays(totalCount);
    };
    
    calculateWorkingDays();
  }, [formData.startDate, formData.endDate, formData.requestType, holidays, isHalfDay]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // If changing request type, reset end date for half-days
    if (name === 'requestType') {
      setFormData(prev => ({
        ...prev,
        [name]: value,
        endDate: value !== 'full-day' ? prev.startDate : prev.endDate
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };
  
  const handleDateChange = (name, date) => {
    console.log(`Setting ${name} to:`, date);
    
    // For half-days, set both start and end date to the same value
    if (name === 'startDate' && isHalfDay) {
      setFormData(prev => ({
        ...prev,
        startDate: date,
        endDate: date
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: date
      }));
    }
  };
  
  const handleRefreshBalance = async () => {
    setError('');
    const stats = await fetchLeaveData(true);
    if (stats) {
      console.log('Manual refresh complete, balance:', stats.remaining);
    }
  };
  
  const handleSubmit = async (e) => {
  e.preventDefault();
  e.stopPropagation(); // Prevent event bubbling
  setError('');
  
  // Add a small delay to prevent accidental submissions
  if (isSubmitting) {
    console.log('Already submitting, ignoring duplicate request');
    return;
  }
  
  // Validate dates
  if (!formData.startDate) {
    setError('Please select a start date');
    return;
  }
  
  if (!isHalfDay && !formData.endDate) {
    setError('Please select an end date');
    return;
  }
  
  // Validate date range for full days
  if (!isHalfDay && new Date(formData.endDate) < new Date(formData.startDate)) {
    setError('End date cannot be before start date');
    return;
  }
  
  // Validate leave type
  if (!formData.leaveTypeId) {
    setError('Please select a leave type');
    return;
  }
  
  // Validate calculated days
  if (calculatedDays <= 0) {
    setError('Your selected date range contains no working days');
    return;
  }
  
  // Refresh leave data before submission
  try {
    const freshData = await fetchLeaveData(false);
    
    if (freshData && calculatedDays > freshData.remaining) {
      setError(`Insufficient leave balance. You have ${freshData.remaining} days available.`);
      return;
    }
  } catch (err) {
    console.warn('Failed to refresh data before submission, using current balance');
  }
  
  if (calculatedDays > leaveStats.remaining) {
    setError(`Insufficient leave balance. You have ${leaveStats.remaining} days available.`);
    return;
  }
  
  console.log('Form submission initiated by user action'); // Add logging
  setIsSubmitting(true);
  
  try {
    const formatDateForAPI = (date) => {
      const d = new Date(date);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    };
    
    const leaveRequest = {
      startDate: formatDateForAPI(formData.startDate),
      endDate: formatDateForAPI(isHalfDay ? formData.startDate : formData.endDate),
      leaveTypeId: parseInt(formData.leaveTypeId),
      reason: formData.reason,
      requestType: formData.requestType,
      workingDays: calculatedDays
    };
    
    console.log('Submitting leave request:', leaveRequest);
    
    await leaveService.createLeaveRequest(leaveRequest);
    
    localStorage.setItem('leave_balance_updated', 'true');
    window.dispatchEvent(new CustomEvent('leave-balance-update'));
    
    navigate('/leave-requests', { 
      state: { success: 'Leave request submitted successfully' } 
    });
  } catch (err) {
    const errorMessage = err.response?.data?.message || 'Failed to submit leave request';
    setError(errorMessage);
    setIsSubmitting(false);
  }
};
  
  // Get holidays that fall within the selected date range
  const getSelectedHolidays = () => {
    if (!formData.startDate || !holidays.length) return [];
    
    if (isHalfDay) {
      // For half-days, only check the single selected date
      const selectedDate = new Date(formData.startDate);
      selectedDate.setHours(0, 0, 0, 0);
      
      return holidays.filter(holiday => {
        const holidayDate = new Date(holiday.date);
        holidayDate.setHours(0, 0, 0, 0);
        return holidayDate.getTime() === selectedDate.getTime();
      });
    }
    
    if (!formData.endDate) return [];
    
    const start = new Date(formData.startDate);
    const end = new Date(formData.endDate);
    
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    return holidays.filter(holiday => {
      const holidayDate = new Date(holiday.date);
      holidayDate.setHours(0, 0, 0, 0);
      return holidayDate >= start && holidayDate <= end;
    });
  };
  
  const getRemainingBalance = () => {
    return Math.max(0, leaveStats.remaining - calculatedDays);
  };
  
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const selectedHolidays = getSelectedHolidays();
  
  return (
    <AppLayout>
      <div className="max-w-3xl mx-auto">
        <Card>
          <CardHeader>
            <CardTitle className="text-xl">Request Leave</CardTitle>
          </CardHeader>
          
          <CardContent>
            {error && (
              <div className="bg-red-50 text-red-500 p-3 rounded-md mb-4">
                {error}
                <div className="mt-2">
                  <button 
                    onClick={handleRefreshBalance}
                    className="text-xs text-red-700 underline"
                    type="button"
                  >
                    Try refreshing data
                  </button>
                </div>
              </div>
            )}
            
            {isLoading && (
              <div className="bg-blue-50 text-blue-500 p-3 rounded-md mb-4">
                Loading leave data...
              </div>
            )}
            
            {/* Leave Balance Summary */}
            <div className="mb-6 p-4 bg-indigo-50 border-l-4 border-indigo-500 rounded-md">
              <div className="flex justify-between items-center">
                <h3 className="font-medium">Current Leave Balance</h3>
                <div className="flex items-center gap-2">
                  <div className="text-lg font-bold text-indigo-700">
                    {isLoading ? (
                      <span className="inline-block w-12 bg-indigo-200 animate-pulse">&nbsp;</span>
                    ) : (
                      `${leaveStats.remaining} days`
                    )}
                  </div>
                  <button 
                    onClick={handleRefreshBalance}
                    className="text-xs text-indigo-600 hover:text-indigo-800 underline"
                    disabled={isLoading}
                    type="button"
                  >
                    {isLoading ? 'Refreshing...' : 'Refresh'}
                  </button>
                </div>
              </div>
              <p className="text-sm text-indigo-600 mt-1">
                {leaveStats.used} of {leaveStats.total} leave days used
              </p>
              <div className="text-xs text-indigo-400 mt-1">
                Last updated: {new Date().toLocaleTimeString()}
              </div>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
  {/* Request Type Selection */}
<div className="space-y-2">
  <label htmlFor="requestType" className="text-sm font-medium">
    Request Type
  </label>
  <Select
    id="requestType"
    name="requestType"
    value={formData.requestType}
    onChange={handleChange}
    disabled={isSubmitting || isLoading}
  >
    <SelectOption value="full-day">Full Day(s)</SelectOption>
    <SelectOption value="morning-half">Morning Half-day</SelectOption>
    <SelectOption value="afternoon-half">Afternoon Half-day</SelectOption>
  </Select>
  {isHalfDay && (
    <p className="text-xs text-gray-500">
      Half-day requests are for a single day only
    </p>
  )}
</div>

  {/* Date Pickers */}
  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
    <div className="space-y-2">
      <label className="text-sm font-medium">
        {isHalfDay ? 'Date' : 'Start Date'}
      </label>
      <DatePicker
        date={formData.startDate}
        setDate={date => handleDateChange('startDate', date)}
        disabled={isSubmitting || isLoading}
      />
    </div>
    {!isHalfDay && (
      <div className="space-y-2">
        <label className="text-sm font-medium">End Date</label>
        <DatePicker
          date={formData.endDate}
          setDate={date => handleDateChange('endDate', date)}
          disabled={isSubmitting || isLoading}
        />
      </div>
    )}
  </div>

  {/* Leave Type */}
  <div className="space-y-2">
  <label htmlFor="leaveTypeId" className="text-sm font-medium">
    Leave Type
  </label>
  <Select
    id="leaveTypeId"
    name="leaveTypeId"
    value={formData.leaveTypeId}
    onChange={handleChange}
    disabled={isSubmitting || isLoading}
  >
    <SelectOption value="">Select leave type</SelectOption>
    {leaveTypes.map(type => (
      <SelectOption key={type.id} value={type.id}>
        {type.name}
      </SelectOption>
    ))}
  </Select>
</div>

  {/* Reason */}
  <div className="space-y-2">
    <label htmlFor="reason" className="text-sm font-medium">
      Reason (Optional)
    </label>
    <Input
      id="reason"
      name="reason"
      placeholder="Enter reason for leave"
      value={formData.reason}
      onChange={handleChange}
      disabled={isSubmitting || isLoading}
    />
  </div>

  {/* Selected Period Summary */}
  {formData.startDate && (
    <div className="p-3 bg-blue-50 rounded border border-blue-200 text-sm">
      <p>
        Selected period:{' '}
        <strong>
          {isHalfDay
            ? `${formatDate(formData.startDate)} (${formData.requestType.replace('-', ' ')})`
            : `${formatDate(formData.startDate)} - ${formatDate(formData.endDate)}`}
        </strong>
      </p>
    </div>
  )}

  {/* Leave Calculation Summary */}
  <div className="mt-6 p-4 bg-gray-50 rounded-md">
    <h3 className="font-medium mb-2">Leave Summary</h3>
    <div className="grid grid-cols-2 gap-4">
      <div>
        <p className="text-sm text-gray-500">
          {isHalfDay ? 'Half-day Selected' : 'Total Days Selected'}
        </p>
        <p className="font-medium">
          {totalDays} {totalDays === 1 ? 'day' : 'days'}
        </p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Weekend Days</p>
        <p className="font-medium">{weekendDays}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Holiday Days</p>
        <p className="font-medium">{holidayDays}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Working Days</p>
        <p className="font-medium">{calculatedDays}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Current Leave Balance</p>
        <p className="font-medium">{leaveStats.remaining}</p>
      </div>
      <div>
        <p className="text-sm text-gray-500">Remaining After Request</p>
        <p className="font-medium">{getRemainingBalance()}</p>
      </div>
    </div>

    {isHalfDay && calculatedDays === 0.5 && (
      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
        <p className="text-sm text-blue-700">
          Half-day request will deduct 0.5 days from your balance.
        </p>
      </div>
    )}

    {isHalfDay && calculatedDays === 0 && (weekendDays > 0 || holidayDays > 0) && (
      <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-sm text-yellow-700">
          Notice: The selected date falls on a {weekendDays > 0 ? 'weekend' : 'holiday'} and won’t deduct from your balance.
        </p>
      </div>
    )}
  </div>

  {/* Holidays in Period */}
  {selectedHolidays.length > 0 && (
    <div className="p-4 bg-blue-50 rounded-md">
      <h3 className="font-medium mb-2">Holidays in Selected Period</h3>
      <ul className="text-sm space-y-1">
        {selectedHolidays.map(hol => (
          <li key={hol.id}>
            • {formatDate(hol.date)} - {hol.name}
          </li>
        ))}
      </ul>
    </div>
  )}
            </form>
          </CardContent>
          
          <CardFooter className="flex justify-between">
            <Button
              variant="outline"
              onClick={() => navigate('/leave-requests')}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={isSubmitting || calculatedDays <= 0 || isLoading || leaveTypes.length === 0}
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </AppLayout>
  );
};

export default LeaveRequestForm;